import { useEffect, useRef } from "react";

/**
 * Infinite scroll, measured against the page.
 *
 * The list has no scroll pane of its own -- it grows down the page and the
 * page is what scrolls -- so the sentinel under the list is watched with an
 * IntersectionObserver against the viewport. An observer rooted at the
 * viewport is still clipped by whatever ancestor genuinely scrolls, so the
 * same hook works for a list embedded in a modal body without knowing it is
 * in one.
 *
 * `onReach` is held in a ref, so the observer always calls the current
 * closure and the effect does not have to tear down every render. `deps` is
 * what reconnects it: pass the row count. Reconnecting re-fires the callback
 * when the sentinel is *still* in view after a batch lands, which is what
 * keeps a short first page from stalling -- an observer that is already
 * intersecting reports nothing new on its own.
 *
 * @param {React.RefObject} ref      the sentinel element under the list
 * @param {Function}        onReach  asks for the next batch; guards its own re-entry
 * @param {Array}           deps     reconnect triggers, normally [rows.length, ...]
 * @param {number}          margin   px before the sentinel is actually on screen
 */
export default function useScrollLoadMore(ref, onReach, deps = [], margin = 320) {
  const onReachRef = useRef(onReach);
  onReachRef.current = onReach;

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onReachRef.current();
      },
      { rootMargin: `${margin}px 0px` },
    );
    io.observe(node);
    return () => io.disconnect();
    // ref and margin are stable for the life of a list; deps is the caller's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
